/**
 * Ported as-is from the marketing-site /membership page's Final CTA card
 * (membership-client.tsx → FinalCta), with the background image dropped
 * because the booking-step container already provides its own surface.
 *
 * Icons are inlined here rather than imported so this card stays
 * self-contained — it's a prototype slot, copy + visuals will iterate.
 */

import { useTranslation } from "react-i18next";
import { DsButton, NudgeArrow } from "@/components/ui/ds-button";
import { BookingLanguage } from "@/types/booking";
import { languageMap } from "@/i18n";
import { CareIcon } from "@/components/icons/CareIcon";
import { ActionPlanIcon } from "@/components/icons/ActionPlanIcon";
import { MriScannerIcon } from "@/components/icons/MriScannerIcon";
import { CirclePlusIcon } from "@/components/icons/CirclePlusIcon";
import { useMarket } from "@/hooks/useMarket";

export const FollowUpIcon = ({ size = 28 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M13.2432 0.999943V2.82568"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.6348 0.999943V2.82568"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.2886 14.1869L16.4371 19.2577L18.5856 14.1869"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.6341 1.91293H20.7165C22.2328 1.91293 23.3276 3.36417 22.911 4.82207L21.0937 11.1821C20.4999 13.2603 18.6003 14.6931 16.4388 14.6931C14.2773 14.6931 12.3777 13.2603 11.7839 11.1821L9.96664 4.82207C9.55008 3.36417 10.6449 1.91293 12.1612 1.91293H13.2436"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.73828 17.4315V15.195C3.73828 13.4052 5.18928 11.9543 6.97916 11.9543C8.76904 11.9543 10.22 13.4052 10.22 15.195V19.8904C10.22 21.6078 11.6123 23 13.3298 23C15.0473 23 16.4396 21.6078 16.4396 19.8904V19.2572"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="3.738"
      cy="20.17"
      r="2.739"
      stroke="currentColor"
      strokeWidth="1.2"
    />
  </svg>
);

const GreenCheck = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="7.5" fill="#7B8C3B" />
    <path
      d="M5 8l2 2 4-4"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AheadLogoMark = () => (
  <span
    className="relative flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full md:size-[80px]"
    style={{
      // Pre-cropped & resized to 320×320 WebP (7KB). Source is the
      // marketing-site pill-icon-bg (3228×1816 PNG, 6.6MB) — we cropped a
      // zoomed-in left region offline so the full image never loads.
      // To regenerate, see scripts notes / pillow snippet in commit history.
      backgroundImage: "url(/membership-logo-bg.webp)",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    }}
  >
    <svg
      width="24"
      height="24"
      viewBox="0 0 15 16"
      fill="none"
      aria-hidden="true"
      className="relative md:h-9 md:w-9"
    >
      <path
        d="M11.3742 0H0V1.99316L11.2938 1.71601C12.3158 1.69133 13.1522 2.51707 13.1311 3.53073L12.8861 15.7934H14.8593V3.4586C14.8593 1.54897 13.2976 0 11.3723 0Z"
        fill="white"
      />
      <path
        d="M9.37226 4.30513L0 14.3525L1.42584 15.8028L10.8517 5.66047C11.2325 5.25045 11.2019 4.61264 10.7828 4.24059C10.3732 3.87612 9.74546 3.9046 9.37226 4.30513Z"
        fill="white"
      />
    </svg>
  </span>
);

interface MembershipCardProps {
  language: BookingLanguage;
  onStart?: () => void;
}

export const MembershipCard = ({ language, onStart }: MembershipCardProps) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  // DE market has no imaging — the third pillar must not reference MRI.
  const { market } = useMarket();
  const isGermany = market === "de";

  // Third pillar shares its copy with PackageCard's blood-only feature row
  // (packageCard.addTestsOrMri) so the wording stays in sync across both cards
  // from a single translation key. DE swaps to an imaging-free "add tests" pillar.
  const pillars = [
    {
      tKey: "membershipCard.pillars.swissPhysicians",
      Icon: CareIcon,
    },
    {
      tKey: "membershipCard.pillars.actionPlan",
      Icon: ActionPlanIcon,
    },
    isGermany
      ? { tKey: "packageCard.addTestsAnytime", Icon: CirclePlusIcon }
      : { tKey: "packageCard.addTestsOrMri", Icon: MriScannerIcon },
  ] as const;

  const features = [
    "biomarkers",
    "bloodTests",
    "adaptivePlan",
    "supportTeam",
    "memberPricing",
    "uploadResults",
  ] as const;

  return (
    <div
      className="mx-auto w-full max-w-[780px] rounded-2xl p-5 md:p-8 transition-shadow duration-300 ease-out shadow-card hover:shadow-card-hover"
      style={{
        background: "rgba(255, 255, 255, 0.95)",
        border: "1px solid #ffffff",
      }}
    >
      <div className="flex flex-col items-start gap-6 md:flex-row md:items-stretch md:gap-0">
        {/* Left — branding + price + CTA */}
        <div className="flex w-full shrink-0 flex-col md:w-[320px] md:border-r md:border-black/[0.06] md:pr-8">
          <div className="mb-3 flex items-center gap-3 md:block">
            <AheadLogoMark />
            <h2 className="text-2xl font-normal text-neutral-900 md:mt-3 md:text-[40px] md:leading-[1.1]">
              {t("membershipCard.title")}
            </h2>
          </div>

          <p className="mb-6 text-neutral-900">
            <span className="text-sm">{t("membershipCard.pricePrefix")}</span>
            <span className="text-[28px] leading-[36px] font-medium">299</span>
            <span className="text-sm">{t("membershipCard.priceSuffix")}</span>
          </p>

          <DsButton
            onClick={onStart}
            variant="primary"
            className="group w-full md:mt-auto md:w-auto"
            iconRight={<NudgeArrow />}
          >
            {t("membershipCard.cta")}
          </DsButton>
        </div>

        {/* Right — pillars + checklist */}
        <div className="flex flex-1 flex-col gap-2 md:gap-4 md:pl-8">
          <div className="flex gap-2">
            {pillars.map(({ tKey, Icon }) => (
              <div
                key={tKey}
                /* No top padding — the SVGs read as visually "low" because their
                 glyphs sit below center within their viewBox, so the pillars
                 column ends up looking offset against the logo on the left.
                 Drop the top pad and keep a small bottom pad on md+ for rhythm. */
                className="flex flex-1 flex-col items-center gap-1 px-1 pt-0 pb-0 text-center md:pb-2"
              >
                <div className="flex size-10 items-center justify-center text-neutral-500">
                  <Icon size={28} />
                </div>
                <p className="text-[12px] leading-[1.2] font-normal text-neutral-700 whitespace-pre-line">
                  {t(tKey)}
                </p>
              </div>
            ))}
          </div>

          <div className="flex flex-col">
            {features.map((key) => (
              <div key={key} className="flex h-9 items-center gap-2 px-2">
                <GreenCheck size={16} />
                <span className="text-[14px] text-neutral-900">
                  {t(`membershipCard.features.${key}`)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
